package com.evoapps.evoshift.widgets

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.unit.dp
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.role_new
import evoshift.composeapp.generated.resources.role_new_hint
import evoshift.composeapp.generated.resources.role_none
import evoshift.composeapp.generated.resources.role_other
import evoshift.composeapp.generated.resources.roster_role
import org.jetbrains.compose.resources.stringResource

/** The "Other role…" entry of [RoleField]'s menu; no workplace role can be a control character. */
private const val OTHER_ROLE = "\u0000other"

/**
 * A role (a shift's, a template's, a person's), SH-130: picked from the workplace's
 * [availableRoles] when it has any, with "Other role…" at the end of the list for one that is not
 * there yet (a field for it appears under the menu). A workplace without roles gets a plain text
 * field. [optional] adds "No role" at the top of the menu (it stands for ""). A role that is no
 * longer on the workplace's list (renamed, removed) stays selectable.
 */
@Composable
fun RoleField(
    role: String,
    onRoleChange: (String) -> Unit,
    availableRoles: List<String>,
    modifier: Modifier = Modifier,
    label: String = stringResource(Res.string.roster_role),
    optional: Boolean = false,
    imeAction: ImeAction = ImeAction.Next
) {
    val roles = availableRoles.filter { it.isNotBlank() }.distinct()
    if (roles.isEmpty()) {
        EvoTextField(value = role, onValueChange = onRoleChange, label = label, imeAction = imeAction, modifier = modifier)
        return
    }
    // Typing a new role: the menu shows "Other role…" and the field under it holds the name.
    var typing by rememberSaveable { mutableStateOf(false) }
    // The role this field started with keeps its place in the menu while a new one is typed.
    val initial = remember { role }
    val options = buildList {
        if (optional) add("")
        addAll(roles)
        listOf(initial, role).forEach { r -> if (r.isNotBlank() && r !in this && !(typing && r == role)) add(r) }
        add(OTHER_ROLE)
    }
    val none = stringResource(Res.string.role_none)
    val other = stringResource(Res.string.role_other)
    Column(modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(12.dp)) {
        EvoDropdown(
            label = label,
            options = options,
            selected = if (typing) OTHER_ROLE else role.takeIf { optional || it.isNotBlank() },
            onSelected = { picked ->
                if (picked == OTHER_ROLE) {
                    if (!typing) onRoleChange("")
                    typing = true
                } else {
                    typing = false
                    onRoleChange(picked)
                }
            },
            optionLabel = { option ->
                when (option) {
                    OTHER_ROLE -> other
                    "" -> none
                    else -> option
                }
            }
        )
        if (typing) {
            EvoTextField(
                value = role,
                onValueChange = onRoleChange,
                label = stringResource(Res.string.role_new),
                supportingText = stringResource(Res.string.role_new_hint),
                imeAction = imeAction
            )
        }
    }
}
